import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Lightbulb, Printer } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Any = Record<string, any>;

interface Pos {
  id?: string;
  bezeichnung: string;
  position_typ: string;
  betrag_gutachten: string;
  betrag_anerkannt: string;
  kuerzung_grund: string;
  begruendung: string;
  bild_urls: string[];
}

interface Props {
  akte: Any;
  stellungnahme: Any;
}

const n = (v: any): number | null => (v === '' || v == null ? null : Number(v));
const eur = (v: any) =>
  v == null || v === '' ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const esc = (v: any) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

// Erklärung-Vorschlag: Bezeichnung -> Kategorie der Wissensbasis
const KAT_MAP: [string, string[]][] = [
  ['stundenverrechnungssaetze', ['stundenverrechnung', 'lohn', 'stundensatz', 'arbeitslohn', 'arbeitswert']],
  ['verbringungskosten', ['verbring', 'lackiererei']],
  ['upe', ['upe', 'ersatzteil', 'aufschlag']],
  ['minderwert', ['minderwert', 'wertminderung']],
  ['restwert', ['restwert']],
  ['nachbesichtigung', ['nachbesichtigung']],
  ['honorar', ['honorar', 'sachverständigen', 'gutachterkosten']],
];

export default function StellungnahmePositionen({ akte, stellungnahme }: Props) {
  const [positionen, setPositionen] = useState<Pos[]>([]);
  const [einleitung, setEinleitung] = useState<string>(stellungnahme?.einleitung ?? '');
  const [wissen, setWissen] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!stellungnahme?.id) return;
    setLoading(true);
    const [p, w] = await Promise.all([
      supabase.from('stellungnahme_positionen').select('*').eq('stellungnahme_id', stellungnahme.id).order('reihenfolge', { ascending: true }),
      supabase.from('stellungnahme_wissen').select('id, kategorie, titel, argumentation, rechtsgrundlage').eq('aktiv', true),
    ]);
    setPositionen(
      (p.data ?? []).map((r: any) => ({
        id: r.id,
        bezeichnung: r.bezeichnung ?? '',
        position_typ: r.position_typ ?? 'schaden',
        betrag_gutachten: r.betrag_gutachten ?? '',
        betrag_anerkannt: r.betrag_anerkannt ?? '',
        kuerzung_grund: r.kuerzung_grund ?? '',
        begruendung: r.begruendung ?? '',
        bild_urls: r.bild_urls ?? [],
      })),
    );
    setWissen(w.data ?? []);
    setLoading(false);
  }, [stellungnahme?.id]);

  useEffect(() => {
    load();
  }, [load]);

  function addPos(typ = 'schaden') {
    setPositionen((p) => [
      ...p,
      { bezeichnung: '', position_typ: typ, betrag_gutachten: '', betrag_anerkannt: '', kuerzung_grund: '', begruendung: '', bild_urls: [] },
    ]);
  }
  function upd(i: number, patch: Partial<Pos>) {
    setPositionen((p) => p.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function del(i: number) {
    setPositionen((p) => p.filter((_, idx) => idx !== i));
  }
  function kuerzung(p: Pos): number | null {
    const g = n(p.betrag_gutachten);
    const a = n(p.betrag_anerkannt);
    if (g == null) return null;
    return Math.max(0, g - (a ?? 0));
  }

  function vorschlag(i: number) {
    const pos = positionen[i];
    const t = (pos.bezeichnung || '').toLowerCase();
    let kat = '';
    for (const [k, keys] of KAT_MAP) if (keys.some((kw) => t.includes(kw))) { kat = k; break; }
    const cand = wissen.filter((w) => (kat ? w.kategorie === kat : true));
    const w = cand[0];
    if (!w) {
      setMsg('Kein passender Baustein gefunden.');
      setTimeout(() => setMsg(null), 1500);
      return;
    }
    upd(i, { begruendung: w.argumentation + (w.rechtsgrundlage ? ` (${w.rechtsgrundlage})` : '') });
  }

  async function speichern() {
    if (!stellungnahme?.id) return;
    setSaving(true);
    await supabase.from('stellungnahmen').update({ einleitung }).eq('id', stellungnahme.id);
    await supabase.from('stellungnahme_positionen').delete().eq('stellungnahme_id', stellungnahme.id);
    if (positionen.length) {
      const rows = positionen.map((p, idx) => ({
        stellungnahme_id: stellungnahme.id,
        akte_id: akte?.id ?? null,
        reihenfolge: idx,
        bezeichnung: p.bezeichnung || null,
        position_typ: p.position_typ,
        betrag_gutachten: n(p.betrag_gutachten),
        betrag_anerkannt: n(p.betrag_anerkannt),
        betrag_kuerzung: kuerzung(p),
        kuerzung_grund: p.kuerzung_grund || null,
        begruendung: p.begruendung || null,
        bild_urls: p.bild_urls,
      }));
      await supabase.from('stellungnahme_positionen').insert(rows);
    }
    setSaving(false);
    setMsg('Gespeichert.');
    setTimeout(() => setMsg(null), 2000);
    await load();
  }

  function druckenGegenueberstellung() {
    const a: any = akte || {};
    const origin = window.location.origin;
    const heute = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
    const kennz = a.fahrzeug_kennzeichen || a.kennzeichen || '';
    const aktennr = a.akten_nr || a.akten_nummer || '';
    const vn = a.versicherungsnummer || a.schadennummer || '';
    const titelAz = aktennr || stellungnahme?.aktenzeichen_ag || '';
    const empf = stellungnahme?.empfaenger_kanzlei || (a.kunde_name ? `Herr ${a.kunde_name}` : '');

    const blocks = positionen
      .map((p, idx) => {
        const g = n(p.betrag_gutachten);
        const ank = n(p.betrag_anerkannt);
        const k = kuerzung(p);
        const bilder = (p.bild_urls || [])
          .slice(0, 4)
          .map((u) => `<img class="pbild" src="${esc(u)}" onerror="this.style.display='none'">`)
          .join('');
        return `
      <div class="pos">
        <div class="pos-h">Position ${idx + 1}: ${esc(p.bezeichnung || '—')}</div>
        <table class="gt">
          <tr><th>Gefordert (Gutachten)</th><th>Anerkannt (Versicherung)</th><th>Kürzung</th></tr>
          <tr><td>${eur(g)}</td><td>${eur(ank)}</td><td class="kred">${eur(k)}</td></tr>
        </table>
        ${p.kuerzung_grund ? `<div class="lbl">Kürzungsbegründung der Versicherung:</div><div class="txt">${esc(p.kuerzung_grund).replace(/\n/g, '<br>')}</div>` : ''}
        <div class="lbl">Sachverständige Stellungnahme:</div><div class="txt">${esc(p.begruendung || '[Erklärung ergänzen]').replace(/\n/g, '<br>')}</div>
        ${bilder ? `<div class="bilder">${bilder}</div>` : ''}
      </div>`;
      })
      .join('');

    const summeG = positionen.reduce((s, p) => s + (n(p.betrag_gutachten) ?? 0), 0);
    const summeK = positionen.reduce((s, p) => s + (kuerzung(p) ?? 0), 0);

    const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Stellungnahme ${esc(aktennr)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;1,400&display=swap');
  @page { size: A4; margin: 18mm 18mm 16mm 22mm; }
  * { box-sizing: border-box; }
  body { font-family:'Open Sans',Arial,sans-serif; color:#1a1a1a; font-size:10.5pt; line-height:1.5; margin:0; }
  .head { display:flex; align-items:stretch; gap:8px; margin-bottom:10px; }
  .head .logo img { height:18mm; width:auto; object-fit:contain; }
  .head .banner { flex:1; background:#1f3a5c; color:#fff; text-align:right; padding:8px 14px; display:flex; flex-direction:column; justify-content:center; }
  .head .banner .b1 { font-weight:600; font-size:14pt; }
  .head .banner .b2 { font-weight:600; font-size:10.5pt; }
  .head .banner .b3 { font-size:8.5pt; font-weight:300; }
  .az { display:flex; justify-content:space-between; font-size:9pt; font-weight:600; border-bottom:0.5pt solid #1f3a5c; padding-bottom:3px; margin:4px 0 10px; }
  .empf { font-weight:600; margin-bottom:10px; }
  h1 { font-size:14pt; font-weight:600; margin:6px 0 8px; }
  .einl { text-align:justify; margin:0 0 12px; }
  .pos { border:0.5pt solid #d8dee6; border-radius:2mm; padding:3mm; margin-bottom:7px; page-break-inside:avoid; }
  .pos-h { font-weight:600; color:#1f3a5c; margin-bottom:4px; }
  table.gt { width:100%; border-collapse:collapse; margin-bottom:5px; }
  table.gt th { background:#eef2f7; color:#33455c; font-size:8pt; text-align:left; padding:2px 6px; border:0.3pt solid #d8dee6; }
  table.gt td { padding:2px 6px; border:0.3pt solid #d8dee6; font-weight:600; }
  table.gt td.kred { color:#9b1c1c; }
  .lbl { font-size:8pt; color:#666; margin-top:4px; }
  .txt { text-align:justify; margin-bottom:2px; }
  .bilder { display:flex; gap:4px; margin-top:4px; flex-wrap:wrap; }
  .bilder .pbild { height:28mm; width:auto; border:0.3pt solid #ccc; border-radius:1mm; }
  .summe { margin-top:8px; font-weight:600; }
  .sig { margin-top:14px; white-space:pre-line; }
</style></head><body>
  <div class="head">
    <div class="logo"><img src="${origin}/logo-dark.png" alt="KOOP" onerror="this.style.display='none'"></div>
    <div class="banner"><div class="b1">Kfz Sachverständigenbüro Koop</div><div class="b2">Inh. Andreas Koop</div><div class="b3">Kfz-Techniker-Meister &amp; Kfz-Sachverständiger</div></div>
  </div>
  <div class="az"><span>AZ: ${esc(aktennr)}${kennz ? '  |  ' + esc(kennz) : ''}${vn ? '  |  VN: ' + esc(vn) : ''}</span><span>Iserlohn, ${esc(heute)}</span></div>
  ${empf ? `<div class="empf">${esc(empf)}</div>` : ''}
  <h1>Stellungnahme zum Gutachten ${esc(titelAz)}</h1>
  <div class="einl">${esc(einleitung || '[Einleitung ergänzen]').replace(/\n/g, '<br>')}</div>
  ${blocks}
  <div class="summe">Summe gefordert: ${eur(summeG)} · Summe Kürzung: ${eur(summeK)}</div>
  <div class="sig">Mit freundlichen Grüßen

Andreas Koop
Kfz Techniker-Meister
Kfz Sachverständiger – Mitglied im MAS
BVSK Akademie</div>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setMsg('Bitte Popups erlauben.'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 600);
  }

  const inputCls = 'w-full rounded-md border border-gray-300 px-2 py-1 text-sm';

  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Positionen · Gegenüberstellung</h3>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-green-600">{msg}</span>}
          <button
            type="button"
            onClick={druckenGegenueberstellung}
            className="flex items-center gap-1 text-xs font-medium text-[#1f3a5c] border border-[#1f3a5c]/40 hover:bg-[#1f3a5c]/5 px-2 py-1 rounded-md"
          >
            <Printer className="w-3.5 h-3.5" /> Drucken
          </button>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-gray-500">Einleitung</span>
        <textarea
          className={inputCls + ' mt-1 min-h-[60px]'}
          value={einleitung}
          onChange={(e) => setEinleitung(e.target.value)}
          placeholder="Einleitender Text der Stellungnahme (Anlass, Auftrag, Sachverhalt) …"
        />
      </label>

      {loading ? (
        <p className="text-xs text-gray-400">Lädt…</p>
      ) : (
        <div className="space-y-3">
          {positionen.map((p, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-2.5 bg-gray-50/50 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 shrink-0">#{i + 1}</span>
                <input
                  className={inputCls}
                  value={p.bezeichnung}
                  onChange={(e) => upd(i, { bezeichnung: e.target.value })}
                  placeholder="Bezeichnung (z.B. Verbringungskosten, Stundenverrechnungssatz, Wertminderung …)"
                />
                <select
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs bg-white shrink-0"
                  value={p.position_typ}
                  onChange={(e) => upd(i, { position_typ: e.target.value })}
                >
                  <option value="schaden">Schaden</option>
                  <option value="honorar">Honorar</option>
                </select>
                <button type="button" onClick={() => del(i)} className="text-gray-400 hover:text-red-600 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-[11px] text-gray-500">
                  Gefordert (€)
                  <input type="number" step="0.01" className={inputCls + ' mt-0.5'} value={p.betrag_gutachten}
                    onChange={(e) => upd(i, { betrag_gutachten: e.target.value })} />
                </label>
                <label className="text-[11px] text-gray-500">
                  Anerkannt (€)
                  <input type="number" step="0.01" className={inputCls + ' mt-0.5'} value={p.betrag_anerkannt}
                    onChange={(e) => upd(i, { betrag_anerkannt: e.target.value })} />
                </label>
                <label className="text-[11px] text-gray-500">
                  Kürzung
                  <div className="mt-0.5 px-2 py-1 text-sm font-semibold text-[#9b1c1c]">{eur(kuerzung(p))}</div>
                </label>
              </div>
              <label className="block text-[11px] text-gray-500">
                Kürzungsbegründung der Versicherung
                <textarea className={inputCls + ' mt-0.5 min-h-[44px]'} value={p.kuerzung_grund}
                  onChange={(e) => upd(i, { kuerzung_grund: e.target.value })}
                  placeholder="Wortlaut/Begründung aus dem Kürzungsschreiben" />
              </label>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">Sachverständige Stellungnahme (Erklärung)</span>
                  <button type="button" onClick={() => vorschlag(i)}
                    className="flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:underline">
                    <Lightbulb className="w-3 h-3" /> Vorschlag aus Wissensbasis
                  </button>
                </div>
                <textarea className={inputCls + ' mt-0.5 min-h-[56px]'} value={p.begruendung}
                  onChange={(e) => upd(i, { begruendung: e.target.value })}
                  placeholder="Erwiderung — per Vorschlag aus der Wissensbasis einsetzbar und anpassbar" />
              </div>
              <label className="block text-[11px] text-gray-500">
                Bilder (eine URL pro Zeile, aus dem Gutachten)
                <textarea className={inputCls + ' mt-0.5 min-h-[40px] font-mono text-[11px]'}
                  value={(p.bild_urls || []).join('\n')}
                  onChange={(e) => upd(i, { bild_urls: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                  placeholder="https://…/lichtbild1.jpg" />
              </label>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => addPos('schaden')}
              className="flex items-center gap-1 text-xs font-medium text-white bg-[#1f3a5c] hover:bg-[#16304d] px-2.5 py-1 rounded-md">
              <Plus className="w-3.5 h-3.5" /> Schaden-Position
            </button>
            <button type="button" onClick={() => addPos('honorar')}
              className="flex items-center gap-1 text-xs font-medium text-[#1f3a5c] border border-[#1f3a5c]/40 hover:bg-[#1f3a5c]/5 px-2.5 py-1 rounded-md">
              <Plus className="w-3.5 h-3.5" /> Honorar-Position
            </button>
            <button type="button" onClick={speichern} disabled={saving}
              className="ml-auto text-xs font-medium text-white bg-[#B91C1C] hover:bg-[#991B1B] px-3 py-1 rounded-md disabled:opacity-50">
              {saving ? 'Speichert…' : 'Positionen speichern'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
